import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ListPlus, AlertCircle } from "lucide-react";

interface Props {
  onRecordsSaved: () => void;
}

interface EscambiaRecord {
  documentNumber: string;
  filingDate: string; // YYYY-MM-DD
  documentType: string;
  bookPage: string;
  grantors: string[];
  grantees: string[];
  legalDescription: string;
  isLikelyOwnerFinance: boolean;
}

// Words that strongly imply a lender / institutional grantee.
const LENDER_KEYWORDS = [
  "BANK", "MORTGAGE", "CREDIT UNION", "FEDERAL SAVINGS", "FEDERAL CREDIT",
  "FINANCIAL", "LENDING", "LENDER", "LOANS", "LOAN COMPANY", "HOME LOANS",
  "HOME EQUITY", "FUNDING", "MERS", "FANNIE", "FREDDIE", "FHA",
  "VETERANS UNITED", "WELLS FARGO", "CHASE", "REGIONS", "FIFTH THIRD",
  "SOUTHSTATE", "ROCKET MORTGAGE", "FREEDOM MORTGAGE", "GUARANTEED RATE",
  "DHI MORTGAGE", "GUILD MORTGAGE", "UNITED WHOLESALE", "CROSSCOUNTRY",
  "ACRISURE", "IMPERIUM", "RELIANCE MORTGAGE", "PRIMARY RESIDENTIAL",
  "TOP FLITE", "LOANUNITED", "CLEAR MORTGAGE", "ZILLOW HOME", "HAVEN HOME",
  "21ST MORTGAGE", "EASY STREET CAPITAL", "DART", "BENCHMARK MORTGAGE",
  "CENTENNIAL BANK", "MORTGAGE RESEARCH",
];

// Suffix tokens that reliably END an entity name when glued to a new capital word.
// Note: TRUST excluded because it appears mid-name (e.g. "MAX LAND TRUST INC").
const ENTITY_END_SUFFIXES = ["INC", "LLC", "CORP", "LTD", "TR", "II", "III", "IV", "JR", "SR"];

const containsLenderKeyword = (s: string): boolean => {
  const upper = s.toUpperCase();
  return LENDER_KEYWORDS.some((kw) => new RegExp(`\\b${kw}\\b`).test(upper));
};

/**
 * Split a "row token" that may contain a glued grantor+grantee pair
 * (e.g. "BILCO INCCENTENNIAL BANK"). Returns [grantorPart, granteePart]
 * or [whole, null] if no confident split is found.
 */
const splitGluedRow = (token: string): [string, string | null] => {
  const trimmed = token.trim();
  if (!trimmed) return [trimmed, null];

  // Look for every (SUFFIX)(UPPERCASE) boundary; pick the earliest where the
  // right side is long enough AND contains a lender/business keyword.
  const suffixGroup = ENTITY_END_SUFFIXES.join("|");
  const re = new RegExp(`\\b(${suffixGroup})(?=[A-Z])`, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(trimmed)) !== null) {
    const splitAt = match.index + match[1].length;
    const left = trimmed.slice(0, splitAt).trim();
    const right = trimmed.slice(splitAt).trim();
    if (right.length < 8) continue; // too short to be a real entity
    if (!containsLenderKeyword(right) && !/\b(LLC|INC|CORP|TRUST)\b/.test(right)) continue;
    return [left, right];
  }
  return [trimmed, null];
};

const classifyParty = (name: string): "grantee" | "grantor" => {
  return containsLenderKeyword(name) ? "grantee" : "grantor";
};

const parseEscambiaText = (rawText: string): EscambiaRecord[] => {
  // Normalize newlines, collapse runs of whitespace inside lines but keep \n.
  const text = rawText.replace(/\r\n/g, "\n").trim();

  // Split on sequence markers like "305V", "306V", ... — they always
  // precede an uppercase letter at the start of a record.
  const blockRe = /(?:^|\n)?(\d{1,5})V(?=[A-Z])/g;
  const positions: { seq: string; start: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = blockRe.exec(text)) !== null) {
    // Adjust start to character right after the "{seq}V" marker.
    positions.push({ seq: m[1], start: m.index + m[0].length });
  }

  const records: EscambiaRecord[] = [];

  for (let i = 0; i < positions.length; i++) {
    const start = positions[i].start;
    const end = i + 1 < positions.length
      ? positions[i + 1].start - (positions[i + 1].seq.length + 2) // back out "\n{seq}V"
      : text.length;
    const block = text.slice(start, end);

    // Pull out date + document type + book/page + doc number.
    // Pattern: MM/DD/YYYY (OPEN END MORTGAGE|MORTGAGE) OR{7digits} {10digit doc}
    const tail = block.match(
      /(\d{2}\/\d{2}\/\d{4})(OPEN END MORTGAGE|MORTGAGE)OR(\d+?)(20\d{7,9})(?=[A-Z]|OR\s|$|\s|\d)([\s\S]*)$/,
    );
    if (!tail) continue;

    const [, rawDate, docType, bookPage, docNumber, legalRaw] = tail;
    const [mm, dd, yyyy] = rawDate.split("/");
    const filingDate = `${yyyy}-${mm}-${dd}`;

    // Parties blob = everything before the date.
    const partiesBlob = block.slice(0, tail.index!).trim();

    // Rows are separated by blank lines (\n\n). Single-row records have none.
    const rowTokens = partiesBlob
      .split(/\n{2,}/)
      .map((t) => t.replace(/\s+/g, " ").trim())
      .filter(Boolean);

    const grantors: string[] = [];
    const grantees: string[] = [];

    for (const row of rowTokens) {
      const [left, right] = splitGluedRow(row);
      if (right) {
        // Glued pair: left = grantor, right = grantee
        if (left) grantors.push(left);
        grantees.push(right);
      } else {
        // Unmerged row — classify by content
        if (classifyParty(left) === "grantee") {
          grantees.push(left);
        } else {
          grantors.push(left);
        }
      }
    }

    // Clean legal description — strip leading "OR 9105 / 1257" cross-refs
    const legalDescription = legalRaw
      .replace(/^OR\s*\d+\s*\/\s*\d+\s*/i, "")
      .replace(/\s+/g, " ")
      .trim();

    // Heuristic: owner-finance if NO grantee is a recognized institutional lender.
    const isLikelyOwnerFinance =
      grantees.length > 0 &&
      grantees.every((g) => !containsLenderKeyword(g));

    records.push({
      documentNumber: docNumber,
      filingDate,
      documentType: docType.trim(),
      bookPage: `OR${bookPage}`,
      grantors,
      grantees,
      legalDescription,
      isLikelyOwnerFinance,
    });
  }

  return records;
};

export function EscambiaCountyTextParser({ onRecordsSaved }: Props) {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const preview = textData.trim() ? parseEscambiaText(textData) : [];
  const previewOwnerFinance = preview.filter((r) => r.isLikelyOwnerFinance).length;

  const handleProcess = async () => {
    if (!textData.trim()) return;
    setIsProcessing(true);
    try {
      const parsed = parseEscambiaText(textData);
      if (parsed.length === 0) {
        toast({
          title: "No records found",
          description: "Could not parse any records from the pasted text.",
          variant: "destructive",
        });
        return;
      }

      // Dedup against existing document_numbers
      const docNumbers = parsed.map((r) => r.documentNumber);
      const existing: { document_number: string }[] = [];
      for (let i = 0; i < docNumbers.length; i += 200) {
        const chunk = docNumbers.slice(i, i + 200);
        const { data, error } = await supabase
          .from("county_research")
          .select("document_number")
          .in("document_number", chunk);
        if (error) throw error;
        if (data) existing.push(...data);
      }
      const existingSet = new Set(existing.map((e) => e.document_number));

      const toInsert = parsed
        .filter((r) => !existingSet.has(r.documentNumber))
        .map((r) => ({
          county: "escambia",
          document_number: r.documentNumber,
          document_type: r.documentType,
          filing_date: r.filingDate,
          grantor_name: r.grantors.join("; ") || null,
          grantee_name: r.grantees.join("; ") || null,
          grantor_address: null,
          grantee_address: null,
          legal_description: r.legalDescription || null,
          notes: `Book/Page: ${r.bookPage}`,
          is_potential_owner_finance: r.isLikelyOwnerFinance,
          confidence_score: r.isLikelyOwnerFinance ? 75 : 40,
          detection_reasons: JSON.stringify(
            r.isLikelyOwnerFinance
              ? ["Escambia County FL", "No institutional lender on grantee side"]
              : ["Escambia County FL", "Institutional mortgage (review for owner-finance)"],
          ),
          address_needs_review: true,
          search_date: new Date().toISOString(),
          status: "new",
          raw_data: { bookPage: r.bookPage, grantors: r.grantors, grantees: r.grantees } as any,
        }));

      const skipped = parsed.length - toInsert.length;

      if (toInsert.length === 0) {
        toast({
          title: "Nothing new",
          description: `All ${parsed.length} records already exist in the database.`,
        });
        return;
      }

      const batchSize = 50;
      let inserted = 0;
      for (let i = 0; i < toInsert.length; i += batchSize) {
        const batch = toInsert.slice(i, i + batchSize);
        const { error } = await supabase.from("county_research").insert(batch);
        if (error) throw error;
        inserted += batch.length;
      }

      toast({
        title: "Import complete",
        description: `Imported ${inserted} Escambia records (${skipped} duplicates skipped). ${
          toInsert.filter((r) => r.is_potential_owner_finance).length
        } flagged as potential owner-finance.`,
      });
      setTextData("");
      onRecordsSaved();
    } catch (err) {
      console.error("Escambia import error:", err);
      toast({
        title: "Import failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Escambia County, FL — Mortgage Bulk Importer</CardTitle>
          <Button
            size="sm"
            onClick={handleProcess}
            disabled={isProcessing || !textData.trim()}
          >
            {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Import
            {preview.length > 0 ? ` (${preview.length})` : ""}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Bulk dump from Escambia clerk records. <strong>No bank/family/business
            filters applied</strong> — every record is imported so you can triage
            owner-finance candidates later. All records are flagged{" "}
            <code>address_needs_review</code> for the worksheet workflow.
          </p>

          <Textarea
            value={textData}
            onChange={(e) => setTextData(e.target.value)}
            placeholder={"305VBILCO INCCENTENNIAL BANK01/15/2026MORTGAGEOR943314842026004028LOT:8-13 BLK:33 SUB:FERRY PASS HEIGHTS306V..."}
            className="min-h-[320px] font-mono text-xs"
          />

          {preview.length > 0 && (
            <div className="text-sm text-muted-foreground space-y-1">
              <p>
                Detected <strong>{preview.length}</strong> records — <strong className="text-green-600">{previewOwnerFinance}</strong>{" "}
                flagged as potential owner-finance (no institutional lender on the
                grantee side).
              </p>
            </div>
          )}

          <Button
            onClick={handleProcess}
            disabled={isProcessing || !textData.trim()}
            className="w-full"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Importing...
              </>
            ) : (
              <>
                <ListPlus className="h-4 w-4 mr-2" />
                Import All Records
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Workflow</AlertTitle>
        <AlertDescription className="text-xs space-y-1">
          <p>1. Paste raw text dump from Escambia clerk search results above.</p>
          <p>2. All records are imported (no filtering) so you don't miss any owner-finance deals.</p>
          <p>3. Records with non-institutional grantees are flagged as <strong>potential owner-finance</strong>.</p>
          <p>4. Use the <strong>Address Worksheet</strong> tab to print blank-address sheets for handwriting.</p>
          <p>5. <strong>FL rule:</strong> when loan amounts are added later, reject mortgages under <strong>$100,000</strong> (FL threshold, vs $75k elsewhere).</p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
